import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Bot, Copy, Plus, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export const Route = createFileRoute('/(protected)/settings/service-accounts/')({
  component: ServiceAccountsPage,
});

function ServiceAccountsPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const listQueryOptions = trpc.serviceAccount.list.queryOptions();
  const { data: serviceAccounts, isLoading } = useQuery(listQueryOptions);

  // Mutations
  const createMutationOptions = trpc.serviceAccount.create.mutationOptions();
  const createMutation = useMutation({
    ...createMutationOptions,
    onSuccess: (data) => {
      setNewApiKey(data.apiKey);
      queryClient.invalidateQueries({ queryKey: trpc.serviceAccount.list.queryKey() });
      toast.success('Service account created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create service account');
    },
  });

  const regenerateMutationOptions = trpc.serviceAccount.regenerateApiKey.mutationOptions();
  const regenerateMutation = useMutation({
    ...regenerateMutationOptions,
    onSuccess: (data) => {
      setNewApiKey(data.apiKey);
      setIsRegenerateDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: trpc.serviceAccount.list.queryKey() });
      toast.success('API key regenerated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to regenerate API key');
    },
  });

  const deleteMutationOptions = trpc.serviceAccount.delete.mutationOptions();
  const deleteMutation = useMutation({
    ...deleteMutationOptions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.serviceAccount.list.queryKey() });
      toast.success('Service account deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete service account');
    },
  });

  const handleCopyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey);
    toast.success('API key copied to clipboard');
  };

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      deleteMutation.mutate({ id });
    }
  };

  const handleRegenerate = (id: string) => {
    setSelectedAccountId(id);
    setIsRegenerateDialogOpen(true);
  };

  const confirmRegenerate = () => {
    if (selectedAccountId) {
      regenerateMutation.mutate({ id: selectedAccountId });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Service Accounts</h1>
          <p className="text-muted-foreground mt-1">
            Create API keys for automated workflow execution
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Service Account
        </Button>
      </div>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            About Service Accounts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Service accounts are automated users that can execute workflows without human login.
          </p>
          <p>
            Use service accounts to schedule workflows, integrate with external systems, or run
            compliance tests automatically.
          </p>
          <p className="font-medium text-foreground">
            Authentication: <code className="bg-muted px-1 py-0.5 rounded">Authorization: Bearer sk_live_...</code>
          </p>
        </CardContent>
      </Card>

      {/* Service Accounts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Service Accounts</CardTitle>
          <CardDescription>
            Manage API keys for automated workflow execution
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : serviceAccounts && serviceAccounts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>API Key</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serviceAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium">{account.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {account.serviceAccountType.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {account.maskedApiKey}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(account.apiKeyCreatedAt), {
                        addSuffix: true,
                      })}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {account.apiKeyLastUsedAt
                        ? formatDistanceToNow(new Date(account.apiKeyLastUsedAt), {
                            addSuffix: true,
                          })
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRegenerate(account.id)}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(account.id, account.name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No service accounts yet</p>
              <p className="text-sm mt-1">Create your first service account to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Service Account Dialog */}
      <CreateServiceAccountDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
      />

      {/* Regenerate API Key Confirmation */}
      <Dialog open={isRegenerateDialogOpen} onOpenChange={setIsRegenerateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate API Key</DialogTitle>
            <DialogDescription>
              This will invalidate the old API key immediately. Any systems using the old key will
              stop working until you update them with the new key.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>
              This action cannot be undone. Make sure you have access to update all systems using
              this API key.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRegenerateDialogOpen(false)}
              disabled={regenerateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRegenerate}
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate API Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show New API Key Dialog */}
      {newApiKey && (
        <Dialog open={!!newApiKey} onOpenChange={() => setNewApiKey(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>API Key Created</DialogTitle>
              <DialogDescription>
                Save this API key now - you won't be able to see it again!
              </DialogDescription>
            </DialogHeader>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                Copy this API key and store it securely. For security reasons, we cannot show it
                again.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label>API Key</Label>
              <div className="flex gap-2">
                <Input
                  value={newApiKey}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopyApiKey(newApiKey)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setNewApiKey(null)}>I've Saved My API Key</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Create Service Account Dialog Component
function CreateServiceAccountDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { name: string; serviceAccountType: string }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState('');
  const [serviceAccountType, setServiceAccountType] = useState<string>('COMPLIANCE_BOT');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    onSubmit({ name: name.trim(), serviceAccountType });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Service Account</DialogTitle>
            <DialogDescription>
              Create a new service account for automated workflow execution
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Compliance Bot"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                A descriptive name for this service account
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={serviceAccountType} onValueChange={setServiceAccountType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPLIANCE_BOT">Compliance Bot</SelectItem>
                  <SelectItem value="INTEGRATION_BOT">Integration Bot</SelectItem>
                  <SelectItem value="MONITORING_BOT">Monitoring Bot</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The type of automation this account will perform
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Service Account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
